import { useEffect } from 'react'
import { track } from './index'

/** [DOM id, 이벤트에 남길 섹션 이름] — 랜딩 화면 순서대로 */
const SECTIONS: [string, string][] = [
  ['top', 'hero'],
  ['technology', 'technology'],
  ['hub', 'hub'],
  ['studio', 'studio'],
  ['vfx', 'vfx'],
  ['contact', 'contact'],
  ['footer', 'footer'],
]

/**
 * 랜딩의 각 섹션이 **처음 화면에 충분히 보였을 때** `Section Viewed` 를 한 번씩 보낸다.
 *
 * "충분히" = 섹션의 절반 이상, 또는 (섹션이 화면보다 길면) 화면의 절반 이상을 채웠을 때.
 * 빠르게 스크롤하며 스쳐 지나간 섹션까지 세지 않도록 0.6초 이상 머물러야 인정한다.
 * 페이지를 새로 열 때마다 다시 센다.
 */
export function useSectionTracking() {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return

    const loadedAt = performance.now()
    const seen = new Set<string>()
    const timers = new Map<string, number>()
    const nameById = new Map(SECTIONS)
    const order = new Map(SECTIONS.map(([id], i) => [id, i + 1]))
    let maxDepth = 0

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).id
          if (seen.has(id)) continue
          const visible = e.intersectionRect.height
          const enough =
            e.isIntersecting &&
            visible >= Math.min(e.boundingClientRect.height * 0.5, window.innerHeight * 0.5)

          if (enough && !timers.has(id)) {
            timers.set(
              id,
              window.setTimeout(() => {
                timers.delete(id)
                if (seen.has(id)) return
                seen.add(id)
                const idx = order.get(id) ?? 0
                maxDepth = Math.max(maxDepth, idx)
                track('Section Viewed', {
                  section: nameById.get(id),
                  section_index: idx,
                  section_count: SECTIONS.length,
                  seconds_since_load: Math.round((performance.now() - loadedAt) / 1000),
                })
              }, 600),
            )
          } else if (!enough && timers.has(id)) {
            window.clearTimeout(timers.get(id))
            timers.delete(id)
          }
        }
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] },
    )

    // 콘텐츠가 늦게 그려질 수 있으므로 지금 있는 요소만 관찰 (섹션은 랜딩과 함께 항상 렌더된다)
    for (const [id] of SECTIONS) {
      const el = document.getElementById(id)
      if (el) io.observe(el)
    }

    return () => {
      io.disconnect()
      timers.forEach((t) => window.clearTimeout(t))
    }
  }, [])
}
